import { useEffect, useRef, useState } from "react";
import {
  X,
  Check,
  Loader2,
  ArrowUpRight,
  LockKeyhole,
  MessageSquare,
  AudioLines,
  FileText,
  FolderOpen,
} from "lucide-react";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useSubscription } from "@/shared/contexts/SubscriptionContext";
import { useBodyScrollLock } from "@/shared/hooks/useBodyScrollLock";
import { PRACTICE_PLAN } from "@/shared/lib/billing";

export function PaywallModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t, locale } = useLanguage();
  const { user } = useAuth();
  const {
    subscription,
    createCheckout,
    openBillingPortal,
    upgradeIntent,
    confirmSubscription,
    completeUpgrade,
    daysRemaining,
    isTrialing,
  } = useSubscription();
  const [cycle, setCycle] = useState<"monthly" | "yearly">("monthly");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<
    "idle" | "confirming" | "delayed"
  >("idle");
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useBodyScrollLock(isOpen);

  const cleanCheckoutUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
  };
  const close = () => {
    cleanCheckoutUrl();
    onCloseRef.current();
  };
  const confirmPaidAccess = async () => {
    setConfirmation("confirming");
    try {
      if (await confirmSubscription()) {
        cleanCheckoutUrl();
        await completeUpgrade();
      } else setConfirmation("delayed");
    } catch {
      setConfirmation("delayed");
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    const previousFocus = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    if (
      new URLSearchParams(window.location.search).get("checkout") === "success"
    )
      void confirmPaidAccess();
    else setConfirmation("idle");
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
      if (event.key !== "Tab") return;
      const controls = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          "button:not([disabled]), input:not([disabled]), a[href]",
        ) ?? [],
      ).filter((element) => element.offsetParent !== null);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen]);

  const intentCopy = (() => {
    const general = {
      en: {
        title: "Keep your work moving",
        body: "Your work is safe. Subscribe to continue from this exact point.",
      },
      uk: {
        title: "Продовжуйте роботу без перерви",
        body: "Вашу роботу збережено. Оформіть підписку, щоб продовжити з цього місця.",
      },
      pl: {
        title: "Kontynuuj bez przerywania pracy",
        body: "Twoja praca jest bezpieczna. Subskrybuj, aby kontynuować od tego miejsca.",
      },
      ro: {
        title: "Continuă fără să pierzi progresul",
        body: "Munca ta este în siguranță. Abonează-te pentru a continua de aici.",
      },
    } as const;
    const practice = {
      en: {
        title: "Continue practicing with Emery",
        body: "Your conversation and draft are safe. Subscribe to continue here.",
      },
      uk: {
        title: "Продовжуйте практику з Emery",
        body: "Вашу розмову та чернетку збережено. Оформіть підписку, щоб продовжити тут.",
      },
      pl: {
        title: "Kontynuuj ćwiczenie z Emery",
        body: "Twoja rozmowa i szkic są bezpieczne. Subskrybuj, aby kontynuować tutaj.",
      },
      ro: {
        title: "Continuă practica alături de Emery",
        body: "Conversația și schița ta sunt în siguranță. Abonează-te pentru a continua aici.",
      },
    } as const;
    const recording = {
      en: {
        title: "Keep working with this recording",
        body: "Your file and progress are safe. Subscribe to continue this recording workflow.",
      },
      uk: {
        title: "Продовжуйте роботу із записом",
        body: "Ваш файл і прогрес збережено. Оформіть підписку, щоб продовжити роботу із записом.",
      },
      pl: {
        title: "Kontynuuj pracę z nagraniem",
        body: "Twój plik i postęp są bezpieczne. Subskrybuj, aby kontynuować pracę z nagraniem.",
      },
      ro: {
        title: "Continuă lucrul cu înregistrarea",
        body: "Fișierul și progresul sunt în siguranță. Abonează-te pentru a continua.",
      },
    } as const;
    const scenario = {
      en: {
        title: "Finish building your scenario",
        body: "Your setup is safe. Subscribe to generate or start this scenario.",
      },
      uk: {
        title: "Завершіть створення сценарію",
        body: "Ваші налаштування збережено. Оформіть підписку, щоб згенерувати або запустити сценарій.",
      },
      pl: {
        title: "Dokończ tworzenie scenariusza",
        body: "Twoje ustawienia są bezpieczne. Subskrybuj, aby wygenerować lub uruchomić scenariusz.",
      },
      ro: {
        title: "Finalizează scenariul",
        body: "Configurația este în siguranță. Abonează-te pentru a genera sau porni scenariul.",
      },
    } as const;
    const language = locale as keyof typeof general;
    if (upgradeIntent?.action.startsWith("practice."))
      return practice[language] ?? practice.en;
    if (upgradeIntent?.action.startsWith("recording."))
      return recording[language] ?? recording.en;
    if (upgradeIntent?.action === "scenario.create")
      return scenario[language] ?? scenario.en;
    return general[language] ?? general.en;
  })();

  const hasContract =
    !!(
      subscription?.stripe_subscription_id ||
      subscription?.paddle_subscription_id ||
      subscription?.revenuecat_product_id
    ) &&
    !["canceled", "incomplete_expired"].includes(subscription?.status ?? "");
  const preservesTrial =
    isTrialing &&
    !!subscription?.trial_end &&
    Date.parse(subscription.trial_end) > Date.now() + 48 * 3600_000;
  const date = (value: string) =>
    new Date(value).toLocaleDateString(locale, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const price =
    cycle === "yearly" ? PRACTICE_PLAN.yearly : PRACTICE_PLAN.monthly;
  const features = [
    {
      Icon: MessageSquare,
      title: "Practice real situations",
      body: PRACTICE_PLAN.features[0],
    },
    {
      Icon: AudioLines,
      title: "Make it your own",
      body: PRACTICE_PLAN.features[1],
    },
    {
      Icon: FileText,
      title: "Learn from conversations",
      body: PRACTICE_PLAN.features[2],
    },
    {
      Icon: FolderOpen,
      title: "See your progress",
      body: PRACTICE_PLAN.features[3],
    },
  ];
  const handleSubscribe = async () => {
    if (busy) return;
    if (!user) {
      (window as any).navigate?.("/signup");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (hasContract) {
        const url = await openBillingPortal();
        if (!url)
          throw new Error(
            "Billing settings could not be opened. Please try again.",
          );
        window.location.assign(url);
      } else {
        const result = await createCheckout({
          plan: cycle === "yearly" ? "individual_yearly" : "individual_monthly",
        });
        if (!result)
          throw new Error("Checkout could not be started. Please try again.");
        if (result.provider !== "stripe")
          throw new Error("Stripe checkout is temporarily unavailable.");
        window.location.assign(result.url);
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Checkout could not be opened. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-6 text-[var(--ink-primary)]">
      <div
        className="absolute inset-0 bg-[var(--rail-bg)]/60"
        onClick={close}
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-paywall-title"
        aria-describedby="subscription-paywall-description"
        className="relative max-h-[94dvh] w-full max-w-[820px] overflow-y-auto rounded-[8px] border border-[var(--line)] bg-[var(--paper-raised)] shadow-2xl animate-page-in motion-reduce:animate-none"
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-[var(--line-soft)] bg-[var(--paper-raised)] px-6 py-3 sm:px-8">
          <span className="ed-mono text-[11px] text-[var(--ink-tertiary)]">
            Personal workspace / Plan & billing
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={close}
            aria-label="Close payment dialog"
            className="eq-button eq-button--ghost !min-h-11 !min-w-11 !p-0"
          >
            <X size={19} />
          </button>
        </header>
        <div className="grid md:grid-cols-[1.1fr_1fr]">
          <div className="px-6 py-7 sm:px-8 sm:py-8">
            <p className="eq-eyebrow mb-4 text-[var(--mark-open-fg)]">
              {PRACTICE_PLAN.name}
            </p>
            <h2
              id="subscription-paywall-title"
              className="ed-serif text-[32px] leading-[1.12] tracking-tight"
            >
              {upgradeIntent
                ? intentCopy.title
                : "More confidence in every conversation."}
            </h2>
            <p
              id="subscription-paywall-description"
              className="mt-4 text-base leading-relaxed text-[var(--ink-secondary)]"
            >
              {upgradeIntent
                ? intentCopy.body
                : "A personal space to rehearse, reflect, and build the skills you use every day."}
            </p>
            <ul className="mt-7 space-y-5">
              {features.map(({ Icon, title, body }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-[4px] bg-[var(--paper-sunken)] text-[var(--mark-open-fg)]">
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{title}</p>
                    <p className="mt-1 text-base leading-relaxed text-[var(--ink-tertiary)] sm:text-sm">
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-7 border-t border-[var(--line-soft)] pt-4 text-xs leading-relaxed text-[var(--ink-muted)]">
              Catalog courses are included. You can also buy courses
              individually. Compliance and team workspaces have their own plans.
            </p>
          </div>
          <div className="order-first flex flex-col border-b border-[var(--line-soft)] bg-[var(--paper-canvas)] px-6 py-7 sm:px-8 sm:py-8 md:order-none md:border-b-0 md:border-l">
            <p className="eq-eyebrow mb-4 text-[var(--mark-open-fg)] md:hidden">
              {PRACTICE_PLAN.name}
            </p>
            <div className="mb-6 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold">
                {hasContract
                  ? "Your subscription"
                  : "Choose your billing cycle"}
              </h3>
              {isTrialing && (
                <span className="rounded-[4px] border border-[var(--line)] bg-[var(--paper-raised)] px-2 py-1 text-xs text-[var(--mark-open-fg)]">
                  Trial · {daysRemaining}d left
                </span>
              )}
            </div>
            {!hasContract && (
              <div
                role="group"
                aria-label="Billing cycle"
                className="grid grid-cols-2 gap-1 rounded-[6px] border border-[var(--line)] bg-[var(--paper-sunken)] p-1"
              >
                {(["monthly", "yearly"] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={cycle === value}
                    onClick={() => setCycle(value)}
                    disabled={busy || confirmation === "confirming"}
                    className={`min-h-11 cursor-pointer rounded-[4px] px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] ${cycle === value ? "bg-[var(--paper-raised)] text-[var(--ink-primary)] shadow-sm" : "text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"}`}
                  >
                    {value === "monthly" ? "Monthly" : "Yearly"}
                  </button>
                ))}
              </div>
            )}
            <div className="my-7">
              <p className="ed-serif text-[42px] leading-none tracking-tight">
                {hasContract
                  ? subscription?.plan === "training_compliance_monthly"
                    ? "Training Compliance"
                    : PRACTICE_PLAN.name
                  : price}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[var(--ink-tertiary)]">
                {hasContract
                  ? "View invoices, update your payment method, or change your subscription in billing settings."
                  : cycle === "yearly"
                    ? "One payment for the year. Renews annually."
                    : "Billed monthly. Renews each month."}
              </p>
            </div>
            <div className="rounded-[6px] border border-[var(--line)] bg-[var(--paper-raised)] p-4 text-sm leading-relaxed">
              <p className="flex items-center gap-2 font-medium">
                <Check
                  size={15}
                  className="text-[var(--mark-done)]"
                  aria-hidden="true"
                />
                {hasContract
                  ? subscription?.status === "past_due" ||
                    subscription?.status === "unpaid"
                    ? "Payment needs attention"
                    : subscription?.cancel_at_period_end
                      ? "Cancellation scheduled"
                      : "Your plan is in place"
                  : preservesTrial
                    ? `First payment on ${date(subscription!.trial_end!)}`
                    : "Subscription starts today"}
              </p>
              <p className="mt-2 text-[var(--ink-tertiary)]">
                {hasContract
                  ? subscription?.current_period_end
                    ? `Current period ends ${date(subscription.current_period_end)}.`
                    : "Manage your renewal in billing settings."
                  : preservesTrial
                    ? "Keep the rest of your free trial. Stripe will confirm your first charge before you subscribe."
                    : "Review the total in Stripe before confirming. Cancel future renewals anytime."}
              </p>
            </div>
            <div className="mt-auto pt-6">
              {confirmation !== "idle" && (
                <p
                  role="status"
                  className="mb-4 rounded-[4px] border border-[var(--line)] bg-[var(--paper-raised)] p-3 text-sm leading-relaxed text-[var(--ink-secondary)]"
                >
                  {confirmation === "confirming"
                    ? "Confirming your subscription with Stripe…"
                    : "Your payment is still being confirmed. You can retry here; your saved work remains available."}
                </p>
              )}
              {error && (
                <p
                  role="alert"
                  className="mb-4 rounded-[4px] bg-[var(--mark-attention-bg)] p-3 text-sm leading-relaxed text-[var(--mark-attention)]"
                >
                  {error}
                </p>
              )}
              <button
                type="button"
                onClick={() =>
                  void (confirmation === "delayed"
                    ? confirmPaidAccess()
                    : handleSubscribe())
                }
                disabled={busy || confirmation === "confirming"}
                className="eq-button eq-button--primary !min-h-12 w-full"
              >
                {busy || confirmation === "confirming" ? (
                  <Loader2
                    size={17}
                    className="animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                ) : (
                  <ArrowUpRight size={17} aria-hidden="true" />
                )}
                {confirmation === "confirming"
                  ? "Confirming access…"
                  : confirmation === "delayed"
                    ? "Retry confirmation"
                    : busy
                      ? t.subscription.processing
                      : hasContract
                        ? "Manage billing"
                        : user
                          ? "Continue to Stripe"
                          : "Create an account"}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[var(--ink-muted)]">
                <LockKeyhole size={12} aria-hidden="true" />
                {hasContract
                  ? "Manage your existing subscription"
                  : "Secure checkout by Stripe"}
              </p>
              {!hasContract && (
                <p className="mt-3 text-center text-xs leading-relaxed text-[var(--ink-muted)]">
                  Have a promotion code? Add it at checkout.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
